import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type Language = "en" | "ja" | "ko";

const defaultTranslations: Record<string, Record<Language, string>> = {
  // Navigation
  "nav.new_arrivals": { en: "New Arrivals", ja: "新着", ko: "신상품" },
  "nav.collection": { en: "Collection", ja: "コレクション", ko: "컬렉션" },
  "nav.about": { en: "About", ja: "について", ko: "소개" },
  "nav.search": { en: "Search", ja: "検索", ko: "검색" },
  "nav.exit": { en: "Exit", ja: "終了", ko: "종료" },
  "nav.cart": { en: "Cart", ja: "カート", ko: "장바구니" },
  
  // Hero
  "hero.season": { en: "Spring/Summer 2026", ja: "2026年春夏", ko: "2026 봄/여름" },
  "hero.title": { en: "UNION COLLECTION", ja: "ユニオン コレクション", ko: "유니온 컬렉션" },
  "hero.subtitle": { en: "Discover our Korean & Asian inspired streetwear collection. Not luxury, just clean.", ja: "韓国とアジアのストリートウェアコレクションをご覧ください。ラグジュアリーではなく、クリーンに。", ko: "한국과 아시아에서 영감을 받은 스트릿웨어 컬렉션을 만나보세요. 럭셔리가 아닌, 깔끔함." },
  "hero.shop_collection": { en: "Shop Collection", ja: "コレクションを見る", ko: "컬렉션 보기" },
  "hero.view_lookbook": { en: "View Lookbook", ja: "ルックブックを見る", ko: "룩북 보기" },
  
  // Products
  "products.featured": { en: "Featured", ja: "注目", ko: "추천" },
  "products.new_arrivals": { en: "NEW ARRIVALS", ja: "新着商品", ko: "신상품" },
  "products.view_all": { en: "View All", ja: "すべて見る", ko: "모두 보기" },
  "products.add_to_cart": { en: "Add to Cart", ja: "カートに追加", ko: "장바구니에 추가" },
  "products.out_of_stock": { en: "Out of Stock", ja: "在庫切れ", ko: "품절" },
  "products.coming_soon": { en: "COMING SOON", ja: "近日公開", ko: "곧 출시" },
  "products.quick_add": { en: "Quick Add", ja: "クイック追加", ko: "빠른 추가" },
  "products.quick_view": { en: "Quick View", ja: "クイックビュー", ko: "빠른 보기" },
  "products.select_size": { en: "Select Size", ja: "サイズを選択", ko: "사이즈 선택" },
  "products.select_a_size": { en: "Select a Size", ja: "サイズを選んでください", ko: "사이즈를 선택하세요" },
  "products.view_full_details": { en: "View Full Details", ja: "詳細を見る", ko: "상세 보기" },
  "products.browse_all": { en: "Browse All Products", ja: "すべての商品を見る", ko: "모든 상품 보기" },
  "products.no_new_arrivals": { en: "No new arrivals in the last 30 days.", ja: "過去30日間の新着商品はありません。", ko: "최근 30일 동안 신상품이 없습니다." },
  "products.no_match": { en: "No products match your filters.", ja: "フィルターに一致する商品がありません。", ko: "필터와 일치하는 상품이 없습니다." },
  "products.scroll_more": { en: "Scroll for more", ja: "スクロールして続きを見る", ko: "스크롤하여 더 보기" },
  "products.count": { en: "Products", ja: "商品", ko: "상품" },
   "products.new": { en: "New", ja: "新着", ko: "신상품" },
   "products.max_stock": { en: "Max stock", ja: "在庫上限", ko: "최대 재고" },
   "products.sold_out": { en: "Sold Out", ja: "売切", ko: "품절" },
   "products.reserve": { en: "Reserve", ja: "予約", ko: "예약" },
  
  // New Arrivals Page
  "new_arrivals.just_dropped": { en: "Just Dropped", ja: "入荷したばかり", ko: "방금 입고" },
  "new_arrivals.title": { en: "NEW ARRIVALS", ja: "新着商品", ko: "신상품" },
  "new_arrivals.subtitle": { en: "Fresh drops from the last 30 days.", ja: "過去30日間の新着商品。", ko: "최근 30일 동안의 신상품." },
  
  // Collection Page
  "collection.title": { en: "EXPLORE COLLECTION", ja: "コレクションを探索", ko: "컬렉션 탐색" },
  "collection.all": { en: "All", ja: "すべて", ko: "전체" },
  "collection.fit": { en: "Fit", ja: "フィット", ko: "핏" },
  "collection.cut": { en: "Cut", ja: "カット", ko: "컷" },
  "collection.size": { en: "Size", ja: "サイズ", ko: "사이즈" },
  "collection.women": { en: "Women", ja: "ウィメンズ", ko: "여성" },
  "collection.men": { en: "Men", ja: "メンズ", ko: "남성" },
  "collection.tops": { en: "Tops", ja: "トップス", ko: "상의" },
  "collection.bottoms": { en: "Bottoms", ja: "ボトムス", ko: "하의" },
  
  // Categories
  "categories.women": { en: "WOMEN", ja: "ウィメンズ", ko: "여성" },
  "categories.men": { en: "MEN", ja: "メンズ", ko: "남성" },
  "categories.shop_now": { en: "Shop Now", ja: "今すぐ購入", ko: "지금 쇼핑하기" },
  
  // About Page
  "about.subtitle": { en: "About Us", ja: "私たちについて", ko: "소개" },
  "about.title": { en: "NOT LUXURY, JUST CLEAN.", ja: "ラグジュアリーではなく、クリーンに。", ko: "럭셔리가 아닌, 깔끔함." },
  "about.intro": { en: "Union is a Korean & Asian inspired streetwear brand built on the belief that style doesn't need a luxury price tag. We create clean, intentional pieces that speak for themselves.", ja: "Unionは、スタイルに高級価格は必要ないという信念のもとに生まれた、韓国とアジアにインスパイアされたストリートウェアブランドです。自らを語る、クリーンで意図的なアイテムを作っています。", ko: "Union은 스타일에 명품 가격표가 필요 없다는 믿음으로 만들어진, 한국과 아시아에서 영감을 받은 스트릿웨어 브랜드입니다. 스스로를 표현하는 깔끔하고 의도적인 아이템을 만듭니다." },
  "about.clean_design": { en: "CLEAN DESIGN", ja: "クリーンなデザイン", ko: "깔끔한 디자인" },
  "about.clean_design_desc": { en: "Every piece is crafted with the attention to detail. No trend-chasing, or fast fashion. Just timeless pieces that work season after season.", ja: "すべてのアイテムは細部までこだわって作られています。トレンドを追いかけたり、ファストファッションではありません。シーズンを超えて使えるタイムレスなアイテムです。", ko: "모든 아이템은 세부 사항까지 신경 써서 제작됩니다. 트렌드 추종이나 패스트 패션이 아닙니다. 시즌을 넘어 사용할 수 있는 타임리스 아이템입니다." },
  "about.accessible_style": { en: "ACCESSIBLE STYLE", ja: "アクセシブルなスタイル", ko: "접근 가능한 스타일" },
  "about.accessible_style_desc": { en: "We believe great style & quality should be accessible to everyone. Our pricing reflects quality without the markup. Real value, not inflated luxury.", ja: "素晴らしいスタイルと品質は誰もがアクセスできるべきだと信じています。価格は品質を反映し、マークアップなし。膨張した高級品ではなく、本当の価値を。", ko: "훌륭한 스타일과 품질은 모든 사람이 접근할 수 있어야 한다고 믿습니다. 가격은 마크업 없이 품질을 반영합니다. 부풀려진 명품이 아닌, 진정한 가치를." },
  "about.cultural_roots": { en: "CULTURAL ROOTS", ja: "文化的ルーツ", ko: "문화적 뿌리" },
  "about.cultural_roots_desc": { en: "Inspired by Korean, Japanese, & Asian streetwear culture, we blend aesthetics with contemporary urban style. East meets West in every collection.", ja: "韓国、日本、アジアのストリートウェア文化にインスパイアされ、美学と現代的なアーバンスタイルを融合させています。すべてのコレクションで東洋と西洋が出会います。", ko: "한국, 일본, 아시아 스트릿웨어 문화에서 영감을 받아 미학과 현대적인 어반 스타일을 융합합니다. 모든 컬렉션에서 동양과 서양이 만납니다." },
  "about.story_label": { en: "The Story", ja: "ストーリー", ko: "스토리" },
  "about.story_title": { en: "OUR JOURNEY", ja: "私たちの旅", ko: "우리의 여정" },
  "about.story_p1": { en: "Union Collection was born from a simple frustration: why does looking good have to cost so much? Growing up between cultures, we saw how Korean and Asian streetwear represented something different. Clean lines, intentional design, and quality that didn't break the bank.", ja: "Union Collectionは、シンプルな不満から生まれました：なぜ良く見えるためにそんなに費用がかかるのか？文化の間で育つ中で、韓国とアジアのストリートウェアが何か違うものを表していることを見ました。クリーンなライン、意図的なデザイン、そして銀行を破らない品質。", ko: "Union Collection은 단순한 불만에서 탄생했습니다: 왜 멋있게 보이려면 그렇게 비싸야 할까요? 문화 사이에서 자라면서 한국과 아시아 스트릿웨어가 다른 무언가를 나타낸다는 것을 보았습니다. 깔끔한 라인, 의도적인 디자인, 그리고 부담스럽지 않은 품질." },
  "about.story_p2": { en: "We started Union to bring that philosophy to everyone. No celebrity endorsements, no luxury markups, no trend-chasing. Just clean, well-made pieces that work for your life.", ja: "私たちはその哲学をすべての人に届けるためにUnionを始めました。セレブのエンドースメントも、高級マークアップも、トレンドチェイシングもありません。ただ、あなたの人生に役立つ、クリーンで丁寧に作られたアイテムだけです。", ko: "우리는 그 철학을 모든 사람에게 전달하기 위해 Union을 시작했습니다. 셀럽 추천도, 명품 마크업도, 트렌드 추종도 없습니다. 당신의 삶에 맞는 깔끔하고 잘 만들어진 아이템만." },
  "about.story_p3": { en: "Every piece we create goes through the same question: \"Would we wear this every day?\" If the answer isn't yes, it doesn't make the cut. That's the Union standard.", ja: "私たちが作るすべてのアイテムは同じ質問を通過します：「毎日これを着るか？」答えがイエスでなければ、カットされません。それがUnionの基準です。", ko: "우리가 만드는 모든 아이템은 같은 질문을 거칩니다: \"매일 이것을 입을까?\" 답이 예가 아니면, 통과하지 못합니다. 그것이 Union 기준입니다." },
  "about.shop_collection": { en: "Shop the Collection", ja: "コレクションを見る", ko: "컬렉션 보기" },
  "about.join_movement": { en: "JOIN THE MOVEMENT", ja: "ムーブメントに参加", ko: "무브먼트에 참여" },
  "about.join_desc": { en: "Be the first to know about new drops, exclusive pieces, and Union updates.", ja: "新着、限定アイテム、Unionのアップデートを最初に知りましょう。", ko: "신상품, 한정 아이템, Union 업데이트를 가장 먼저 알아보세요." },
  "about.explore_collection": { en: "Explore Collection", ja: "コレクションを探索", ko: "컬렉션 탐색" },
  
  // Contact Page
  "contact.title": { en: "CONTACT US", ja: "お問い合わせ", ko: "문의하기" },
  "contact.subtitle": { en: "Have a question or need assistance? We're here to help.", ja: "ご質問やサポートが必要ですか？お手伝いします。", ko: "질문이나 도움이 필요하신가요? 저희가 도와드립니다." },
  "contact.send_message": { en: "SEND A MESSAGE", ja: "メッセージを送る", ko: "메시지 보내기" },
  "contact.name": { en: "Name", ja: "名前", ko: "이름" },
  "contact.email": { en: "Email", ja: "メール", ko: "이메일" },
  "contact.subject": { en: "Subject", ja: "件名", ko: "제목" },
  "contact.message": { en: "Message", ja: "メッセージ", ko: "메시지" },
  "contact.send": { en: "Send Message", ja: "メッセージを送信", ko: "메시지 보내기" },
  "contact.sending": { en: "Sending...", ja: "送信中...", ko: "전송 중..." },
  "contact.get_in_touch": { en: "GET IN TOUCH", ja: "お問い合わせ先", ko: "연락처" },
  "contact.phone": { en: "Phone", ja: "電話", ko: "전화" },
  "contact.address": { en: "Address", ja: "住所", ko: "주소" },
  "contact.hours": { en: "Hours", ja: "営業時間", ko: "영업시간" },
  "contact.follow_us": { en: "Follow Us", ja: "フォローする", ko: "팔로우" },
  
  // Newsletter
  "newsletter.label": { en: "Stay Connected", ja: "つながりを保つ", ko: "연결 유지" },
  "newsletter.title": { en: "JOIN THE LIST", ja: "リストに参加", ko: "리스트 가입" },
  "newsletter.description": { en: "Subscribe to receive exclusive access to new collections, private sales, and style inspiration.", ja: "新コレクション、限定セール、スタイルインスピレーションへの独占アクセスを受け取るために購読してください。", ko: "새 컬렉션, 프라이빗 세일, 스타일 영감에 대한 독점 액세스를 받으려면 구독하세요." },
  "newsletter.placeholder": { en: "Enter your email", ja: "メールアドレスを入力", ko: "이메일 입력" },
  "newsletter.subscribe": { en: "Subscribe", ja: "購読する", ko: "구독하기" },
  "newsletter.consent": { en: "By subscribing, you agree to our Privacy Policy and consent to receive updates.", ja: "購読することで、プライバシーポリシーに同意し、更新情報の受信に同意します。", ko: "구독하면 개인정보 처리방침에 동의하고 업데이트 수신에 동의하게 됩니다." },
  
  // Footer
  "footer.tagline": { en: "Korean & Asian inspired streetwear. Not luxury, just clean.", ja: "韓国とアジアのストリートウェア。ラグジュアリーではなく、クリーンに。", ko: "한국과 아시아에서 영감을 받은 스트릿웨어. 럭셔리가 아닌, 깔끔함." },
  "footer.shop": { en: "Shop", ja: "ショップ", ko: "쇼핑" },
  "footer.women": { en: "Women", ja: "ウィメンズ", ko: "여성" },
  "footer.men": { en: "Men", ja: "メンズ", ko: "남성" },
  "footer.new_arrivals": { en: "New Arrivals", ja: "新着", ko: "신상품" },
  "footer.lookbook": { en: "Lookbook", ja: "ルックブック", ko: "룩북" },
  "footer.help": { en: "Help", ja: "ヘルプ", ko: "도움말" },
  "footer.company": { en: "Company", ja: "会社情報", ko: "회사 정보" },
  "footer.about": { en: "About Us", ja: "会社概要", ko: "회사 소개" },
  "footer.sustainability": { en: "Sustainability", ja: "サステナビリティ", ko: "지속가능성" },
  "footer.careers": { en: "Careers", ja: "採用情報", ko: "채용" },
  "footer.showcases": { en: "Showcases", ja: "ショーケース", ko: "쇼케이스" },
  "footer.contact": { en: "Contact Us", ja: "お問い合わせ", ko: "문의하기" },
  "footer.shipping": { en: "Shipping & Returns", ja: "配送と返品", ko: "배송 및 반품" },
  "footer.sizing": { en: "Size Guide", ja: "サイズガイド", ko: "사이즈 가이드" },
  "footer.faq": { en: "FAQ", ja: "よくある質問", ko: "자주 묻는 질문" },
  "footer.privacy": { en: "Privacy Policy", ja: "プライバシーポリシー", ko: "개인정보 처리방침" },
  "footer.terms": { en: "Terms of Use", ja: "利用規約", ko: "이용약관" },
  "footer.terms_sale": { en: "Terms of Sale", ja: "販売規約", ko: "판매 약관" },
  "footer.copyright": { en: "© 2026 UNION COLLECTION. All rights reserved.", ja: "© 2026 UNION COLLECTION. 全著作権所有。", ko: "© 2026 UNION COLLECTION. 모든 권리 보유." },
  
   // Auth
   "auth.sign_in": { en: "Sign In", ja: "ログイン", ko: "로그인" },
   "auth.sign_up": { en: "Create Account", ja: "アカウント作成", ko: "계정 만들기" },
   "auth.sign_out": { en: "Sign Out", ja: "ログアウト", ko: "로그아웃" },
   "auth.continue_google": { en: "Continue with Google", ja: "Googleで続行", ko: "Google로 계속" },
   "auth.continue_apple": { en: "Continue with Apple", ja: "Appleで続行", ko: "Apple로 계속" },
   "auth.connecting": { en: "Connecting...", ja: "接続中...", ko: "연결 중..." },
   "auth.or": { en: "Or", ja: "または", ko: "또는" },
   "auth.first_name": { en: "First Name", ja: "名", ko: "이름" },
   "auth.last_name": { en: "Last Name", ja: "姓", ko: "성" },
   "auth.email": { en: "Email", ja: "メール", ko: "이메일" },
   "auth.password": { en: "Password", ja: "パスワード", ko: "비밀번호" },
   "auth.password_hint": { en: "At least 8 characters, one uppercase letter, one number, and one symbol", ja: "8文字以上、大文字1文字、数字1文字、記号1文字を含む", ko: "최소 8자, 대문자 1개, 숫자 1개, 기호 1개 포함" },
   "auth.forgot_password": { en: "Forgot your password?", ja: "パスワードをお忘れですか？", ko: "비밀번호를 잊으셨나요?" },
   "auth.no_account": { en: "Don't have an account? Sign up", ja: "アカウントをお持ちでないですか？サインアップ", ko: "계정이 없으신가요? 가입하기" },
   "auth.have_account": { en: "Already have an account? Sign in", ja: "すでにアカウントをお持ちですか？ログイン", ko: "이미 계정이 있으신가요? 로그인" },
   "auth.agree_terms": { en: "By continuing, you agree to the Terms of Service and Privacy Policy of Union Collection.", ja: "続行することで、Union Collectionの利用規約とプライバシーポリシーに同意します。", ko: "계속하면 Union Collection의 이용약관 및 개인정보 처리방침에 동의하는 것입니다." },
   "auth.terms_required": { en: "You must agree to the Terms of Service and Privacy Policy", ja: "利用規約とプライバシーポリシーに同意する必要があります", ko: "이용약관 및 개인정보 처리방침에 동의해야 합니다" },
   "auth.terms_link": { en: "Terms of Service", ja: "利用規約", ko: "이용약관" },
   "auth.privacy_link": { en: "Privacy Policy", ja: "プライバシーポリシー", ko: "개인정보 처리방침" },
   "auth.signin_success": { en: "Signed in successfully!", ja: "ログインしました！", ko: "로그인되었습니다!" },
   "auth.signup_success": { en: "Account created successfully!", ja: "アカウントが作成されました！", ko: "계정이 생성되었습니다!" },
   "auth.exists_error": { en: "An account with this email already exists. Please sign in instead.", ja: "このメールアドレスのアカウントは既に存在します。代わりにログインしてください。", ko: "이 이메일로 이미 계정이 존재합니다. 대신 로그인하세요." },
   "auth.loading": { en: "Loading...", ja: "読み込み中...", ko: "로딩 중..." },
   
   // Password Reset
   "auth.reset_title": { en: "Reset Password", ja: "パスワードをリセット", ko: "비밀번호 재설정" },
   "auth.reset_description": { en: "Enter your email address and we'll send you a link to reset your password", ja: "メールアドレスを入力すると、パスワード再設定用のリンクが送信されます", ko: "이메일 주소를 입력하면 비밀번호 재설정 링크를 보내드립니다" },
   "auth.reset_sent_title": { en: "Check your email", ja: "メールをご確認ください", ko: "이메일을 확인하세요" },
   "auth.reset_sent_desc": { en: "Check your email for a password reset link", ja: "パスワード再設定リンクのメールをご確認ください", ko: "비밀번호 재설정 링크 이메일을 확인하세요" },
   "auth.reset_sent_message": { en: "We've sent a password reset link to {{email}}. Please check your inbox and follow the instructions.", ja: "{{email}}にパスワード再設定リンクを送信しました。受信トレイを確認して、指示に従ってください。", ko: "{{email}}로 비밀번호 재설정 링크를 보냈습니다. 받은 편지함을 확인하고 지시를 따르세요." },
   "auth.send_reset_link": { en: "Send Reset Link", ja: "リセットリンクを送信", ko: "재설정 링크 보내기" },
   "auth.sending": { en: "Sending...", ja: "送信中...", ko: "전송 중..." },
   "auth.back_signin": { en: "Back to Sign In", ja: "ログインに戻る", ko: "로그인으로 돌아가기" },
   "auth.please_enter_email": { en: "Please enter your email address", ja: "メールアドレスを入力してください", ko: "이메일 주소를 입력하세요" },
   "auth.reset_email_sent": { en: "Password reset email sent!", ja: "パスワード再設定メールが送信されました！", ko: "비밀번호 재설정 이메일이 전송되었습니다!" },
   "auth.email_address": { en: "Email Address", ja: "メールアドレス", ko: "이메일 주소" },
  
  // Profile
  "profile.title": { en: "My Profile", ja: "マイプロフィール", ko: "내 프로필" },
  "profile.orders": { en: "Order History", ja: "注文履歴", ko: "주문 내역" },
  "profile.admin": { en: "Admin Panel", ja: "管理パネル", ko: "관리자 패널" },
  
  // Cart
  "cart.title": { en: "YOUR CART", ja: "カート", ko: "장바구니" },
  "cart.empty": { en: "Your cart is empty.", ja: "カートは空です。", ko: "장바구니가 비어 있습니다." },
  "cart.checkout": { en: "Checkout", ja: "チェックアウト", ko: "결제하기" },
  "cart.continue_shopping": { en: "← Continue Shopping", ja: "← 買い物を続ける", ko: "← 쇼핑 계속하기" },
  "cart.product": { en: "Product", ja: "商品", ko: "상품" },
  "cart.quantity": { en: "Quantity", ja: "数量", ko: "수량" },
  "cart.price": { en: "Price", ja: "価格", ko: "가격" },
  "cart.total": { en: "Total", ja: "合計", ko: "합계" },
  "cart.size": { en: "Size", ja: "サイズ", ko: "사이즈" },
  "cart.remove": { en: "Remove", ja: "削除", ko: "삭제" },
  "cart.order_summary": { en: "ORDER SUMMARY", ja: "注文概要", ko: "주문 요약" },
  "cart.subtotal": { en: "Subtotal", ja: "小計", ko: "소계" },
  "cart.shipping": { en: "Shipping", ja: "配送", ko: "배송" },
  "cart.free": { en: "Free", ja: "無料", ko: "무료" },
  "cart.free_shipping_note": { en: "Free shipping on orders over $150", ja: "$150以上のご注文で送料無料", ko: "$150 이상 주문 시 무료 배송" },
  "cart.promo_code": { en: "Promo Code", ja: "プロモーションコード", ko: "프로모션 코드" },
  "cart.enter_code": { en: "Enter code", ja: "コードを入力", ko: "코드 입력" },
  "cart.apply": { en: "Apply", ja: "適用", ko: "적용" },
  "cart.discount": { en: "Discount", ja: "割引", ko: "할인" },
  "cart.taxes_note": { en: "Taxes calculated at checkout", ja: "税金はチェックアウト時に計算されます", ko: "세금은 결제 시 계산됩니다" },
  "cart.processing": { en: "Processing...", ja: "処理中...", ko: "처리 중..." },
  
  // Showcases
  "showcases.subtitle": { en: "Pop-up Events", ja: "ポップアップイベント", ko: "팝업 이벤트" },
  "showcases.title": { en: "SHOWCASES", ja: "ショーケース", ko: "쇼케이스" },
  "showcases.description": { en: "Discover our pop-up events and exclusive showcases. Experience UNION in person.", ja: "ポップアップイベントや限定ショーケースをご覧ください。UNIONを直接体験してください。", ko: "팝업 이벤트와 독점 쇼케이스를 발견하세요. UNION을 직접 경험해보세요." },
  "showcases.no_events_title": { en: "No Upcoming Events", ja: "予定されているイベントはありません", ko: "예정된 이벤트 없음" },
  "showcases.no_events_description": { en: "We don't have any scheduled showcases at the moment. Check back soon for upcoming pop-up events and exclusive experiences.", ja: "現在予定されているショーケースはありません。近日中に開催されるポップアップイベントや限定体験をお楽しみに。", ko: "현재 예정된 쇼케이스가 없습니다. 곧 진행될 팝업 이벤트와 독점 경험을 기대해주세요." },
  
  // Preorder / Reserve
  "preorder.notify_me": { en: "Notify Me When Available", ja: "入荷通知を受け取る", ko: "입고 알림 받기" },
  "preorder.email_placeholder": { en: "Enter your email", ja: "メールアドレスを入力", ko: "이메일 입력" },
  "preorder.signed_up": { en: "You're on the list!", ja: "リストに追加されました！", ko: "리스트에 추가되었습니다!" },
  
  // Shipping Page
  "shipping.title": { en: "Shipping & Returns", ja: "配送と返品", ko: "배송 및 반품" },
  "shipping.shipping": { en: "Shipping", ja: "配送", ko: "배송" },
  "shipping.processing_time": { en: "Processing Time", ja: "処理時間", ko: "처리 시간" },
  "shipping.processing_desc": { en: "Orders are processed within 1-2 business days. Orders placed after 2pm EST will be processed the next business day.", ja: "注文は1〜2営業日以内に処理されます。東部標準時午後2時以降のご注文は翌営業日に処理されます。", ko: "주문은 1-2영업일 내에 처리됩니다. 동부 표준시 오후 2시 이후 주문은 다음 영업일에 처리됩니다." },
  "shipping.domestic": { en: "Domestic Shipping (US)", ja: "国内配送（米国）", ko: "국내 배송 (미국)" },
  "shipping.domestic_standard": { en: "Standard Shipping (Up to a month): $12", ja: "標準配送（最大1ヶ月）：$12", ko: "표준 배송 (최대 한 달): $12" },
  "shipping.domestic_free": { en: "Free Standard Shipping on orders over $150", ja: "$150以上のご注文で送料無料", ko: "$150 이상 주문 시 무료 표준 배송" },
  "shipping.international": { en: "International Shipping", ja: "国際配送", ko: "해외 배송" },
  "shipping.canada_mexico": { en: "Canada & Mexico (Up to a month): $20", ja: "カナダ・メキシコ（最大1ヶ月）：$20", ko: "캐나다 및 멕시코 (최대 한 달): $20" },
  "shipping.europe": { en: "Europe (Up to a month): $35", ja: "ヨーロッパ（最大1ヶ月）：$35", ko: "유럽 (최대 한 달): $35" },
  "shipping.asia": { en: "Asia Pacific (Up to a month): $35", ja: "アジア太平洋（最大1ヶ月）：$35", ko: "아시아 태평양 (최대 한 달): $35" },
  "shipping.rest_world": { en: "Rest of World (Up to a month): $45", ja: "その他の国（最大1ヶ月）：$45", ko: "기타 국가 (최대 한 달): $45" },
  "shipping.customs_note": { en: "International orders may be subject to customs duties and taxes, which are the responsibility of the customer.", ja: "国際注文は関税や税金が課される場合があり、お客様のご負担となります。", ko: "해외 주문에는 관세 및 세금이 부과될 수 있으며, 이는 고객의 책임입니다." },
  "shipping.returns": { en: "Returns", ja: "返品", ko: "반품" },
  "shipping.returns_intro": { en: "We want you to love your Union Collection pieces. If you're not completely satisfied, we accept returns within 30 days of delivery.", ja: "Union Collectionの商品を気に入っていただきたいと思っています。完全にご満足いただけない場合は、配達後30日以内の返品を承ります。", ko: "Union Collection 제품을 마음에 들어하시길 바랍니다. 완전히 만족하지 못하시면 배송 후 30일 이내에 반품을 받습니다." },
  "shipping.return_policy": { en: "Return Policy", ja: "返品ポリシー", ko: "반품 정책" },
  "shipping.return_unworn": { en: "Items must be unworn, unwashed, and in original condition with tags attached", ja: "商品は未着用、未洗濯で、タグが付いた元の状態でなければなりません", ko: "상품은 미착용, 미세탁 상태이며 태그가 부착된 원래 상태여야 합니다" },
  "shipping.return_packaging": { en: "Items must be returned in original packaging", ja: "商品は元のパッケージで返品する必要があります", ko: "상품은 원래 포장으로 반품해야 합니다" },
  "shipping.return_sale": { en: "Sale items are final sale and cannot be returned", ja: "セール品は最終販売であり、返品できません", ko: "세일 상품은 최종 판매이며 반품할 수 없습니다" },
  "shipping.return_accessories": { en: "Accessories are final sale for hygiene reasons", ja: "アクセサリーは衛生上の理由から最終販売です", ko: "액세서리는 위생상의 이유로 최종 판매입니다" },
  "shipping.how_to_return": { en: "How to Return", ja: "返品方法", ko: "반품 방법" },
  "shipping.return_step1": { en: "Contact us at support@unioncollection.net with your order number", ja: "support@unioncollection.net に注文番号をご連絡ください", ko: "support@unioncollection.net으로 주문 번호와 함께 연락해 주세요" },
  "shipping.return_step2": { en: "We'll send you a prepaid return label (US orders only)", ja: "前払いの返品ラベルをお送りします（米国のご注文のみ）", ko: "선불 반품 라벨을 보내드립니다 (미국 주문만 해당)" },
  "shipping.return_step3": { en: "Pack your items securely and drop off at any carrier location", ja: "商品を安全に梱包し、配送業者に持ち込んでください", ko: "상품을 안전하게 포장하고 운송업체에 맡겨주세요" },
  "shipping.return_step4": { en: "Refunds are processed within 5-7 business days of receiving your return", ja: "返品を受け取ってから5〜7営業日以内に返金が処理されます", ko: "반품 수령 후 5-7영업일 이내에 환불이 처리됩니다" },
  "shipping.refund_method": { en: "Refund Method", ja: "返金方法", ko: "환불 방법" },
  "shipping.refund_desc": { en: "Refunds will be issued to the original payment method. Shipping costs are non-refundable unless the return is due to our error.", ja: "返金は元のお支払い方法に発行されます。当社のエラーによる返品でない限り、送料は返金されません。", ko: "환불은 원래 결제 수단으로 발행됩니다. 당사의 오류로 인한 반품이 아닌 한 배송비는 환불되지 않습니다." },
  "shipping.exchanges": { en: "Exchanges", ja: "交換", ko: "교환" },
  "shipping.exchanges_intro": { en: "Need a different size? We're happy to exchange your item for a different size, subject to availability.", ja: "違うサイズが必要ですか？在庫状況に応じて、喜んでサイズ交換いたします。", ko: "다른 사이즈가 필요하신가요? 재고 상황에 따라 다른 사이즈로 기꺼이 교환해 드립니다." },
  "shipping.exchanges_contact": { en: "To request an exchange, contact us at support@unioncollection.net with your order number and the size you need.", ja: "交換をご希望の場合は、support@unioncollection.net に注文番号と必要なサイズをお知らせください。", ko: "교환을 요청하시려면 support@unioncollection.net으로 주문 번호와 필요한 사이즈를 알려주세요." },
  
  // FAQ Page
  "faq.title": { en: "FAQ", ja: "よくある質問", ko: "자주 묻는 질문" },
  "faq.subtitle": { en: "Find answers to commonly asked questions. Can't find what you're looking for? Contact us.", ja: "よくある質問への回答をご覧ください。お探しのものが見つかりませんか？お問い合わせください。", ko: "자주 묻는 질문에 대한 답변을 찾아보세요. 찾는 것이 없으신가요? 문의해 주세요." },
  "faq.no_items": { en: "No FAQs available.", ja: "FAQはありません。", ko: "FAQ가 없습니다." },
  "faq.still_questions": { en: "Still Have Questions?", ja: "まだ質問がありますか？", ko: "아직 질문이 있으신가요?" },
  "faq.team_help": { en: "Our team is here to help. Reach out and we'll get back to you within 24 hours.", ja: "私たちのチームがお手伝いします。24時間以内にご連絡いたします。", ko: "저희 팀이 도와드립니다. 연락 주시면 24시간 이내에 답변드리겠습니다." },
  "faq.contact_us": { en: "Contact Us", ja: "お問い合わせ", ko: "문의하기" },
  
  // Legal Pages
  "legal.privacy_policy": { en: "Privacy Policy", ja: "プライバシーポリシー", ko: "개인정보 처리방침" },
  "legal.terms_of_use": { en: "Terms of Use", ja: "利用規約", ko: "이용약관" },
  "legal.terms_of_sale": { en: "Terms of Sale", ja: "販売規約", ko: "판매 약관" },
  "legal.no_document": { en: "No document uploaded yet", ja: "ドキュメントがまだアップロードされていません", ko: "아직 문서가 업로드되지 않았습니다" },
  "legal.admin_upload": { en: "Administrators can upload a PDF in the admin settings", ja: "管理者は管理設定でPDFをアップロードできます", ko: "관리자가 관리 설정에서 PDF를 업로드할 수 있습니다" },
  
  // Lookbook Page
  "lookbook.label": { en: "Lookbook & Stories", ja: "ルックブック＆ストーリー", ko: "룩북 & 스토리" },
  "lookbook.title": { en: "THE CULTURE", ja: "カルチャー", ko: "컬처" },
  "lookbook.intro": { en: "Union Collection's roots lie in Asian streetwear traditions, particularly Korean and Japanese influences. The brand emphasizes real value over inflated luxury, promoting a \"clean\" aesthetic in its apparel.", ja: "Union Collectionのルーツは、特に韓国と日本の影響を受けたアジアのストリートウェアの伝統にあります。ブランドは膨張した高級品よりも本物の価値を重視し、アパレルにおいて「クリーン」な美学を推進しています。", ko: "Union Collection의 뿌리는 특히 한국과 일본의 영향을 받은 아시아 스트릿웨어 전통에 있습니다. 브랜드는 부풀려진 명품보다 진정한 가치를 강조하며, 의류에서 \"깔끔한\" 미학을 추구합니다." },
  "lookbook.collection_label": { en: "FW26", ja: "FW26", ko: "FW26" },
  "lookbook.collection_title": { en: "THE COLLECTION", ja: "コレクション", ko: "컬렉션" },
  "lookbook.shop_now": { en: "Shop Now", ja: "今すぐ購入", ko: "지금 쇼핑하기" },
  "lookbook.stories_label": { en: "Stories", ja: "ストーリー", ko: "스토리" },
  "lookbook.stories_title": { en: "FROM THE STUDIO", ja: "スタジオから", ko: "스튜디오에서" },
  "lookbook.no_stories": { en: "No stories yet. Check back soon!", ja: "まだストーリーはありません。近日公開予定！", ko: "아직 스토리가 없습니다. 곧 확인해 주세요!" },
  "lookbook.philosophy_label": { en: "Our Philosophy", ja: "私たちの哲学", ko: "우리의 철학" },
  "lookbook.philosophy_title": { en: "EAST MEETS WEST", ja: "東洋と西洋の融合", ko: "동양과 서양의 만남" },
  "lookbook.philosophy_desc": { en: "Union was born from a simple belief: that the best of Korean and Asian streetwear aesthetics could be made accessible to everyone. We don't chase trends or luxury price tags. We create timeless pieces that speak for themselves.", ja: "Unionは、韓国とアジアのストリートウェアの美学の最高のものを誰もがアクセスできるようにできるという単純な信念から生まれました。私たちはトレンドや高級価格を追いかけません。自らを語るタイムレスなアイテムを作っています。", ko: "Union은 한국과 아시아 스트릿웨어 미학의 최고를 모든 사람이 접근할 수 있게 만들 수 있다는 단순한 믿음에서 탄생했습니다. 우리는 트렌드나 명품 가격표를 쫓지 않습니다. 스스로를 표현하는 타임리스 아이템을 만듭니다." },
  "lookbook.cta_title": { en: "JOIN THE MOVEMENT", ja: "ムーブメントに参加", ko: "무브먼트에 참여" },
  "lookbook.cta_desc": { en: "Be the first to know about new drops, exclusive pieces, and Union updates.", ja: "新着、限定アイテム、Unionのアップデートを最初に知りましょう。", ko: "신상품, 한정 아이템, Union 업데이트를 가장 먼저 알아보세요." },
  "lookbook.explore": { en: "Explore Collection", ja: "コレクションを探索", ko: "컬렉션 탐색" },
  
  // Size Guide Page
  "sizeguide.title": { en: "Size Guide", ja: "サイズガイド", ko: "사이즈 가이드" },
  "sizeguide.how_to_measure": { en: "How to Measure", ja: "測り方", ko: "측정 방법" },
  "sizeguide.chest": { en: "Chest", ja: "胸囲", ko: "가슴" },
  "sizeguide.chest_desc": { en: "Measure around the fullest part of your chest, keeping the tape horizontal.", ja: "胸の最も膨らんだ部分を水平にテープで測ってください。", ko: "가슴의 가장 넓은 부분을 테이프를 수평으로 유지하며 측정하세요." },
  "sizeguide.waist": { en: "Waist", ja: "ウエスト", ko: "허리" },
  "sizeguide.waist_desc": { en: "Measure around your natural waistline, keeping the tape comfortably loose.", ja: "自然なウエストラインを、テープを心地よくゆるめに測ってください。", ko: "자연스러운 허리선을 테이프를 편안하게 느슨하게 유지하며 측정하세요." },
  "sizeguide.hips": { en: "Hips", ja: "ヒップ", ko: "엉덩이" },
  "sizeguide.hips_desc": { en: "Measure around the fullest part of your hips, keeping the tape horizontal.", ja: "ヒップの最も膨らんだ部分を水平にテープで測ってください。", ko: "엉덩이의 가장 넓은 부분을 테이프를 수평으로 유지하며 측정하세요." },
  "sizeguide.inseam": { en: "Inseam", ja: "股下", ko: "안쪽 솔기" },
  "sizeguide.inseam_desc": { en: "Measure from the crotch seam to the bottom of the leg along the inside seam.", ja: "股の縫い目から脚の内側に沿って裾まで測ってください。", ko: "가랑이 솔기에서 안쪽 솔기를 따라 바지 밑단까지 측정하세요." },
  "sizeguide.fit_philosophy": { en: "Our Fit Philosophy", ja: "フィットの哲学", ko: "핏 철학" },
  "sizeguide.fit_intro": { en: "UNION designs with an intentionally relaxed, oversized aesthetic inspired by Korean streetwear. Our pieces are meant to drape and flow, not cling.", ja: "UNIONは、韓国のストリートウェアにインスパイアされた、意図的にリラックスしたオーバーサイズの美学でデザインしています。私たちのアイテムは、体にフィットするのではなく、ドレープして流れるように作られています。", ko: "UNION은 한국 스트릿웨어에서 영감을 받은 의도적으로 여유롭고 오버사이즈한 미학으로 디자인합니다. 저희 제품은 몸에 달라붙는 것이 아니라 드레이프되고 흐르도록 만들어졌습니다." },
  "sizeguide.oversized": { en: "Oversized Fit", ja: "オーバーサイズフィット", ko: "오버사이즈 핏" },
  "sizeguide.oversized_desc": { en: "Designed to be worn loose. If you prefer a more fitted look, consider sizing down.", ja: "ゆったりと着用するようにデザインされています。よりフィットした見た目をお好みの場合は、サイズダウンをご検討ください。", ko: "넉넉하게 입도록 디자인되었습니다. 더 타이트한 룩을 원하시면 사이즈를 낮추는 것을 고려해 보세요." },
  "sizeguide.regular": { en: "Regular Fit", ja: "レギュラーフィット", ko: "레귤러 핏" },
  "sizeguide.regular_desc": { en: "True to size with a relaxed but not oversized silhouette.", ja: "標準サイズで、リラックスしたがオーバーサイズではないシルエット。", ko: "여유롭지만 오버사이즈는 아닌 실루엣의 표준 사이즈." },
  "sizeguide.slim": { en: "Slim Fit", ja: "スリムフィット", ko: "슬림 핏" },
  "sizeguide.slim_desc": { en: "A closer fit while maintaining comfort. Size up for more room.", ja: "快適さを維持しながらよりフィットしたシルエット。ゆとりが欲しい場合はサイズアップしてください。", ko: "편안함을 유지하면서 더 타이트한 핏. 여유를 원하시면 사이즈업하세요." },
  "sizeguide.still_unsure": { en: "Still unsure? Contact us at support@unioncollection.net and we'll help you find your perfect fit.", ja: "まだ迷っていますか？support@unioncollection.netにお問い合わせいただければ、最適なサイズを見つけるお手伝いをします。", ko: "아직 확신이 없으신가요? support@unioncollection.net으로 연락주시면 완벽한 핏을 찾는 데 도움드리겠습니다." },
  
  // Track Order
  "footer.track_order": { en: "Track Order", ja: "注文追跡", ko: "주문 추적" },
  "track.hero_label": { en: "Order Status", ja: "注文状況", ko: "주문 상태" },
  "track.title": { en: "Track Your Order", ja: "注文を追跡", ko: "주문 추적하기" },
  "track.subtitle": { en: "Enter your order details below to view the current status of your shipment.", ja: "配送状況を確認するには、以下に注文の詳細を入力してください。", ko: "배송 현황을 확인하려면 아래에 주문 세부 정보를 입력하세요." },
  "track.find_order": { en: "Find Your Order", ja: "注文を検索", ko: "주문 찾기" },
  "track.order_id": { en: "Order ID", ja: "注文ID", ko: "주문 ID" },
  "track.order_id_placeholder": { en: "e.g., a1b2c3d4-e5f6-7890-abcd-ef1234567890", ja: "例: a1b2c3d4-e5f6-7890-abcd-ef1234567890", ko: "예: a1b2c3d4-e5f6-7890-abcd-ef1234567890" },
  "track.and_or": { en: "and / or", ja: "または", ko: "또는" },
  "track.email": { en: "Email Address", ja: "メールアドレス", ko: "이메일 주소" },
  "track.searching": { en: "Searching...", ja: "検索中...", ko: "검색 중..." },
  "track.track_order": { en: "Track Order", ja: "注文を追跡", ko: "주문 추적" },
  "track.enter_details_error": { en: "Please enter an order ID or email address", ja: "注文IDまたはメールアドレスを入力してください", ko: "주문 ID 또는 이메일 주소를 입력하세요" },
  "track.not_found_toast": { en: "Order not found. Please check your details and try again.", ja: "注文が見つかりません。詳細を確認して再度お試しください。", ko: "주문을 찾을 수 없습니다. 세부 정보를 확인하고 다시 시도하세요." },
  "track.error_toast": { en: "Something went wrong. Please try again.", ja: "問題が発生しました。もう一度お試しください。", ko: "문제가 발생했습니다. 다시 시도해 주세요." },
  "track.status_pending": { en: "Pending", ja: "保留中", ko: "대기 중" },
  "track.status_processing": { en: "Processing", ja: "処理中", ko: "처리 중" },
  "track.status_paid": { en: "Paid", ja: "支払済", ko: "결제 완료" },
  "track.status_shipped": { en: "Shipped", ja: "発送済", ko: "배송 중" },
  "track.status_delivered": { en: "Delivered", ja: "配達済", ko: "배달 완료" },
  "track.status_cancelled": { en: "Cancelled", ja: "キャンセル済", ko: "취소됨" },
  "track.step_placed": { en: "Placed", ja: "注文済", ko: "주문 완료" },
  "track.tracking_number": { en: "Tracking Number", ja: "追跡番号", ko: "추적 번호" },
  "track.track_on_usps": { en: "Track on USPS", ja: "USPSで追跡", ko: "USPS에서 추적" },
  "track.order_date": { en: "Order Date", ja: "注文日", ko: "주문일" },
  "track.last_updated": { en: "Last Updated", ja: "最終更新", ko: "마지막 업데이트" },
  "track.order_items": { en: "Order Items", ja: "注文商品", ko: "주문 상품" },
  "track.qty": { en: "Qty", ja: "数量", ko: "수량" },
  "track.total": { en: "Total", ja: "合計", ko: "합계" },
  "track.not_found_title": { en: "Order Not Found", ja: "注文が見つかりません", ko: "주문을 찾을 수 없습니다" },
  "track.not_found_desc": { en: "We couldn't find an order matching those details. Please double-check your order ID or email address.", ja: "入力された情報と一致する注文が見つかりませんでした。注文IDまたはメールアドレスをご確認ください。", ko: "해당 정보와 일치하는 주문을 찾을 수 없습니다. 주문 ID 또는 이메일 주소를 다시 확인해 주세요." },

   // Common
   "common.loading": { en: "Loading...", ja: "読み込み中...", ko: "로딩 중..." },
   "common.save": { en: "Save Changes", ja: "変更を保存", ko: "변경 사항 저장" },
   "common.connecting": { en: "Connecting...", ja: "接続中...", ko: "연결 중..." },
   "common.added_to_cart": { en: "added to cart", ja: "カートに追加しました", ko: "장바구니에 추가됨" },
   
   // Countdown Banner
   "banner.now": { en: "NOW!", ja: "今すぐ！", ko: "지금!" },
   "banner.countdown": { en: "Days till launch", ja: "ローンチまで", ko: "출시까지" },
   "banner.days": { en: "d", ja: "日", ko: "일" },
   "banner.hours": { en: "h", ja: "時", ko: "시" },
   "banner.minutes": { en: "m", ja: "分", ko: "분" },
   "banner.seconds": { en: "s", ja: "秒", ko: "초" },
   
   // Product Card Statuses
   "product.sold_out": { en: "Sold Out", ja: "売切", ko: "품절" },
   "product.reserve": { en: "Reserve", ja: "予約", ko: "예약" },
};

// Helper to get default translations for a language
const getDefaultsForLanguage = (lang: Language): Record<string, string> => {
  const result: Record<string, string> = {};
  Object.keys(defaultTranslations).forEach((key) => {
    result[key] = defaultTranslations[key][lang] || defaultTranslations[key].en;
  });
  return result;
};

// Module-level cache to deduplicate fetches across component instances
let translationCache: Record<string, { data: Record<string, string>; timestamp: number }> = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
let inflight: Record<string, Promise<Record<string, string>>> = {};

export const useTranslations = () => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem("language");
    return (stored as Language) || "en";
  });
  // Initialize with defaults immediately to prevent flash of translation keys
  const [translations, setTranslations] = useState<Record<string, string>>(() => {
    const stored = localStorage.getItem("language") as Language || "en";
    // Use cache if available
    const cached = translationCache[stored];
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }
    return getDefaultsForLanguage(stored);
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTranslations = async () => {
      // Check cache first
      const cached = translationCache[language];
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        setTranslations(cached.data);
        setLoading(false);
        return;
      }

      // Deduplicate in-flight requests
      if (!inflight[language]) {
        inflight[language] = (async () => {
          try {
            const { data, error } = await supabase
              .from("translations")
              .select("key, value")
              .eq("language_code", language);

            if (error) throw error;

            const dbTranslations: Record<string, string> = {};
            data?.forEach((t) => {
              dbTranslations[t.key] = t.value;
            });

            const merged: Record<string, string> = {};
            Object.keys(defaultTranslations).forEach((key) => {
              merged[key] = dbTranslations[key] || defaultTranslations[key][language] || defaultTranslations[key].en;
            });

            translationCache[language] = { data: merged, timestamp: Date.now() };
            return merged;
          } catch {
            const fallback = getDefaultsForLanguage(language);
            translationCache[language] = { data: fallback, timestamp: Date.now() };
            return fallback;
          } finally {
            delete inflight[language];
          }
        })();
      }

      try {
        const result = await inflight[language];
        if (result) setTranslations(result);
      } catch {
        setTranslations(getDefaultsForLanguage(language));
      } finally {
        setLoading(false);
      }
    };

    fetchTranslations();
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("language", lang);
  }, []);

  const t = useCallback(
    (key: string, fallback?: string): string => {
      return translations[key] || fallback || key;
    },
    [translations]
  );

  return { t, language, setLanguage, loading };
};

export const languageOptions: { code: Language; label: string; nativeLabel: string }[] = [
  { code: "en", label: "English", nativeLabel: "English" },
  { code: "ja", label: "Japanese", nativeLabel: "日本語" },
  { code: "ko", label: "Korean", nativeLabel: "한국어" },
];
